import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router'
import { PublicClientApplication } from '@azure/msal-browser'
import { useDialogs } from '../components/ui'
import { config } from '../config/environment'
import { authApiService, type OAuthLinksResponse, type OAuthProvider } from '../services/authApiService'
import { ApiError } from '../services/BaseApiService'
import { colors, fontSize, radius, spacing } from '../styles/theme'

/** sessionStorage flag AuthRedirectPage reads: the Microsoft redirect is a link from Settings, not a sign-in. */
export const OAUTH_LINK_KEY = 'docm.oauthLink'

const LABELS: Record<OAuthProvider, string> = { microsoft: 'Microsoft', google: 'Google', apple: 'Apple' }

/**
 * Settings > Security: the federated accounts that sign in to this account. Linking Microsoft goes through the same
 * MSAL redirect as sign-in, flagged so /auth-redirect links instead (a work account under another address, or one whose
 * tenant does not verify email, can sign in afterwards). Unlinking the only way in is refused by the API.
 */
export default function SignInMethods() {
  const dialogs = useDialogs()
  const navigate = useNavigate()
  const location = useLocation()
  const [data, setData] = useState<OAuthLinksResponse | null>(null)
  const [busy, setBusy] = useState(false)
  const notice = (location.state as { signInMethodNotice?: { ok: boolean; message: string } } | null)?.signInMethodNotice
  const [message, setMessage] = useState<{ ok: boolean; message: string } | null>(notice ?? null)

  async function load() {
    try { setData(await authApiService.oauthSignInMethods()) } catch { setData({ hasPassword: true, links: [] }) }
  }

  useEffect(() => {
    load()
    // The notice came once through router state; drop it so a reload does not show it again.
    if (notice) navigate(location.pathname + location.search, { replace: true, state: null })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  async function linkMicrosoft() {
    if (!config.microsoftClientId) return
    setBusy(true)
    setMessage(null)
    try {
      const msal = new PublicClientApplication({
        auth: { clientId: config.microsoftClientId, redirectUri: window.location.origin + '/auth-redirect' },
      })
      await msal.initialize()
      const { nonce } = await authApiService.oauthNonce()
      sessionStorage.setItem(OAUTH_LINK_KEY, 'microsoft')
      await msal.loginRedirect({ scopes: ['openid', 'profile', 'email'], nonce, prompt: 'select_account' })
    } catch (err) {
      sessionStorage.removeItem(OAUTH_LINK_KEY)
      setMessage({ ok: false, message: err instanceof ApiError ? err.message : 'Could not start the Microsoft sign-in.' })
      setBusy(false)
    }
  }

  async function unlink(provider: OAuthProvider) {
    const ok = await dialogs.confirm({
      title: `Unlink ${LABELS[provider]}?`,
      message: `You will no longer be able to sign in with this ${LABELS[provider]} account.`,
      confirmLabel: 'Unlink',
      danger: true,
    })
    if (!ok) return
    setBusy(true)
    setMessage(null)
    try {
      await authApiService.oauthUnlink(provider)
      setMessage({ ok: true, message: `${LABELS[provider]} unlinked.` })
      await load()
    } catch (err) {
      setMessage({ ok: false, message: err instanceof ApiError ? err.message : 'Unlink failed.' })
    } finally {
      setBusy(false)
    }
  }

  const linked = new Map((data?.links ?? []).map(l => [l.provider, l]))
  const rows: OAuthProvider[] = ['microsoft', ...(['google', 'apple'] as OAuthProvider[]).filter(p => linked.has(p))]

  return (
    <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: radius.md, padding: spacing.lg, marginBottom: spacing.lg }}>
      <div style={{ fontSize: fontSize.md, fontWeight: 600, color: colors.text }}>Sign-in methods</div>
      <div style={{ fontSize: fontSize.sm, color: colors.textSecondary, marginTop: spacing.xs, marginBottom: spacing.md }}>
        {data?.hasPassword === false
          ? 'Your account has no password, so keep at least one linked account.'
          : 'Accounts you can sign in with besides your email and password.'}
      </div>

      {message && (
        <div role={message.ok ? 'status' : 'alert'} style={{
          background: message.ok ? colors.successLight : colors.errorLight,
          color: message.ok ? colors.success : colors.error,
          border: `1px solid ${message.ok ? colors.successBorder : '#fecaca'}`,
          borderRadius: radius.sm, padding: `${spacing.sm} ${spacing.md}`, fontSize: fontSize.sm, marginBottom: spacing.md,
        }}>{message.message}</div>
      )}

      {data && rows.map(p => {
        const link = linked.get(p)
        const canLink = p === 'microsoft' && !!config.microsoftClientId
        if (!link && !canLink) return null
        return (
          <div key={p} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: `${spacing.sm} 0`, borderTop: `1px solid ${colors.border}` }}>
            <div>
              <div style={{ fontSize: fontSize.base, color: colors.text, fontWeight: 500 }}>{LABELS[p]}</div>
              <div style={{ fontSize: fontSize.sm, color: colors.textSecondary }}>
                {link ? `Linked ${new Date(link.linkedAt).toLocaleDateString()}` : 'Not linked'}
              </div>
            </div>
            {link ? (
              <button type="button" disabled={busy} onClick={() => unlink(p)} style={{
                padding: '6px 14px', background: 'none', border: `1px solid ${colors.border}`, borderRadius: radius.sm,
                fontSize: fontSize.sm, color: colors.text, cursor: busy ? 'not-allowed' : 'pointer',
              }}>Unlink</button>
            ) : (
              <button type="button" disabled={busy} onClick={linkMicrosoft} style={{
                padding: '6px 14px', background: colors.primary, border: 'none', borderRadius: radius.sm,
                fontSize: fontSize.sm, fontWeight: 600, color: colors.onPrimary, cursor: busy ? 'not-allowed' : 'pointer',
              }}>Link Microsoft account</button>
            )}
          </div>
        )
      })}
      {dialogs.element}
    </div>
  )
}
